// ◉ xlVISION · Powering Business Together!
// © ExcelVision, Lda. — Braga, Portugal
// ( ·:·●●·:· ) xlVISION | Powering Business Together!
//
// ============================================================================
// A lista de pedidos e o «Planear» — o coração do protótipo
// ============================================================================
//
// 📗 fonte 5.3.3.1, transcrita e implementada gesto a gesto:
//   «lista com cores, tags e marcadores»                    → `Linha`
//   «prioridades de 0 a 10 … subir ou descer na lista»      → botões ▲ ▼
//   «botão do lado direito e mudar estados»                 → `MenuContexto`
//   «copiar / colar / duplicar … indicando motivo ou origem»→ `MenuContexto`
//   «mudar itinerário … janela com os dias»                 → `MenuContexto`
//   «opção "Planear" … mostra as 10 melhores opções»        → `PainelPlanear`
//
// ⚠️ O QUE PERSISTE E O QUE NÃO — E O ECRÃ DIZ QUAL É QUAL
//   ✅ **Escolher uma opção GRAVA** (C4): `POST /api/plano/reservar` cria as alocações
//   dos dias, o pedido passa a «reservado» e a decisão fica no log de auditoria. Numa
//   transacção, com o índice único da base a arbitrar quem chega primeiro.
//
//   ⚠️ **Mudar prioridade e mudar estado continuam só no ecrã.** São escritas que
//   precisam da tabela `pedido`, que espera D-009/D-010/D-011. O aviso no topo da lista
//   di-lo, e é a única forma honesta de entregar a ordenação antes disso.
//
//   ⚠️ E **reserva prévia não é serviço confirmado**: a efectivação é C5.
// ============================================================================

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ESTADOS,
  comPrioridade,
  ordenarFila,
  prontoAPlanear,
  razaoParaNaoPlanear,
  tags,
  type Pedido,
} from './pedido.js';
import { IconeChevron, IconeFechar, IconeVoo } from './icones.js';
import { cantoInicial, useArrastavel, useEscape } from './janela.js';

/** Largura de omissão de cada janela. O CSS usa a mesma; ver `.janela--*`. */
const LARGURA_LISTA = 560;
const LARGURA_OPCOES = 700;

type EventoRato<T> = React.MouseEvent<T>;

/** Onde o menu de contexto abriu, e para que pedido. */
interface Contexto {
  readonly pedido: Pedido;
  readonly x: number;
  readonly y: number;
}

export interface RecomendacaoRecebida {
  readonly opcoes: readonly {
    readonly executor:
      | { readonly tipo: 'viatura'; readonly viatura: number }
      | { readonly tipo: 'subcontratado'; readonly id: string; readonly nome: string };
    readonly localidadeActual: number;
    readonly kmAoLocal: number;
    readonly horasAteDisponivel: number;
    readonly motorista: number;
    readonly diasAoServicoNaSemana: number;
    readonly implicaTrocaDeMotorista: boolean;
    /** Os identificadores REAIS — é com eles que a reserva nomeia o que se escolheu. */
    readonly viaturaId: string | null;
    readonly motoristaId: string | null;
    /** F2: requisitos que não foi possível verificar. Avisam, não excluem. */
    readonly requisitosPorVerificar?: readonly string[];
    readonly custo: {
      readonly total: number;
      readonly parcelas: readonly { readonly nome: string; readonly valor: number; readonly origem: string }[];
      readonly naoSomado: readonly string[];
    };
    readonly avisos: readonly string[];
    readonly distanciaAproximada: boolean;
  }[];
  readonly funil: Readonly<Record<string, number>>;
  readonly excluidos: Readonly<Record<string, number>>;
  readonly limitacoes: readonly string[];
  readonly ms: number;
  /**
   * Se este ambiente consegue gravar a reserva.
   *
   * ⚠️ **Não é opcional por comodidade: é a resposta a um defeito real.** Um ambiente
   * sem base de dados devolvia 404 ao `POST`, e o planeador só o descobria DEPOIS de
   * clicar em «Escolher» — já com a decisão tomada. Um ecrã não deve oferecer o que o
   * servidor não consegue servir.
   *
   * `undefined` quando a API é anterior a isto: aí presume-se que **não** grava, porque
   * prometer de menos é melhor do que prometer de mais.
   */
  readonly escritaDisponivel?: boolean;
}

export function Pedidos({
  fechar,
  nomeLocalidade,
  nomeMotorista,
  numeroViatura,
  recarregar,
}: {
  fechar: () => void;
  /** Índice → `MAD — Madrid`. O painel mostra o nome completo, como foi pedido. */
  nomeLocalidade: (i: number) => string;
  nomeMotorista: (i: number) => string;
  numeroViatura: (i: number) => string;
  /**
   * Volta a pedir o mapa ao servidor depois de uma escrita.
   *
   * ⚠️ Sem isto o planeador reservava e a grelha ao lado continuava a mostrar a viatura
   * livre: a cache do servidor era invalidada, mas o navegador já tinha os dados. Ver
   * `App.tsx`.
   */
  recarregar?: (() => void) | undefined;
}) {
  const [pedidos, definirPedidos] = useState<readonly Pedido[] | null>(null);
  const [erro, definirErro] = useState<string | null>(null);
  const [contexto, definirContexto] = useState<Contexto | null>(null);
  const [aPlanear, definirAPlanear] = useState<Pedido | null>(null);

  useEffect(() => {
    const abortar = new AbortController();
    fetch('/api/pedidos', { signal: abortar.signal })
      .then(async (r) => {
        if (!r.ok) {
          definirErro(`a API respondeu ${r.status}`);
          return;
        }
        const c = (await r.json()) as { pedidos: Pedido[] };
        definirPedidos(ordenarFila(c.pedidos));
      })
      .catch((e: unknown) => {
        if (e instanceof Error && e.name === 'AbortError') return;
        definirErro(e instanceof Error ? e.message : String(e));
      });
    return () => abortar.abort();
  }, []);

  // Fechar o menu ao clicar fora. Sem isto, o menu ficava aberto e o clique seguinte
  // ia para o que estivesse por baixo — o tipo de coisa que só se nota a usar.
  useEffect(() => {
    if (contexto === null) return;
    const fecharMenu = (): void => definirContexto(null);
    window.addEventListener('click', fecharMenu);
    window.addEventListener('scroll', fecharMenu, true);
    return () => {
      window.removeEventListener('click', fecharMenu);
      window.removeEventListener('scroll', fecharMenu, true);
    };
  }, [contexto]);

  const mudar = useCallback((id: string, f: (p: Pedido) => Pedido) => {
    definirPedidos((lista) =>
      lista === null ? null : ordenarFila(lista.map((p) => (p.id === id ? f(p) : p))),
    );
  }, []);

  const { posicao, aoPressionar, aArrastar } = useArrastavel(() =>
    cantoInicial(
      'direita',
      { largura: LARGURA_LISTA, altura: 520 },
      { largura: window.innerWidth, altura: window.innerHeight },
    ),
  );
  // ⚠️ `useCallback` e não uma função nova por render: o `useEscape` tem-na nas
  // dependências, logo sem isto o ouvinte era removido e reposto em cada render.
  // Escape fecha **a janela de cima primeiro** — as opções antes da lista.
  const aoEscapar = useCallback(() => {
    if (aPlanear !== null) definirAPlanear(null);
    else fechar();
  }, [aPlanear, fechar]);
  useEscape(aoEscapar);

  return (
    <>
      {/* ⚠️ Enquanto a janela das opções está aberta, a LISTA ESCONDE-SE.
          Não é economia de código: 560 + 700 px de janelas num ecrã de 1600 não
          deixavam mapa nenhum, e ver o mapa é justamente o pedido. A lista não é
          precisa para comparar opções — o pedido está identificado no cabeçalho da
          outra janela. Fechar as opções trá-la de volta, no mesmo sítio. */}
      <section
        className="janela janela--pedidos"
        aria-label="Pedidos por planear"
        style={{ left: posicao.x, top: posicao.y, display: aPlanear !== null ? 'none' : undefined }}
      >
        <div
          className={`janela-cab${aArrastar ? ' janela-cab--agarrada' : ''}`}
          onPointerDown={aoPressionar}
        >
          <strong className="menu-titulo">
            Pedidos por planear
            {pedidos !== null ? <span className="menu-conta">{pedidos.length}</span> : null}
          </strong>
          <button type="button" className="btn btn--icone" onClick={fechar} aria-label="Fechar">
            <IconeFechar em={1} />
          </button>
        </div>

        <div className="janela-corpo">
          {/* ⚠️ O aviso não é rodapé: está em cima, antes de alguém mexer em algo. */}
          <p className="pedidos-aviso">
            <strong>Mudar prioridade e estado fica só no ecrã.</strong> Precisam da tabela
            de pedidos, que espera resposta da GVA (D-009 a D-011). ✅ Já <strong>escolher
            uma opção grava</strong>: cria a reserva prévia no mapa e escreve no log de
            auditoria.
          </p>

          {erro !== null ? (
            <p className="pedidos-erro">Não foi possível ler os pedidos: {erro}</p>
          ) : null}
          {pedidos === null && erro === null ? <p className="pedidos-vazio">a carregar…</p> : null}

          {pedidos !== null ? (
            <ol className="pedidos-lista">
              {pedidos.map((p) => (
                <Linha
                  key={p.id}
                  pedido={p}
                  subir={() => mudar(p.id, (x) => comPrioridade(x, +1))}
                  descer={() => mudar(p.id, (x) => comPrioridade(x, -1))}
                  menu={(e) => {
                    e.preventDefault();
                    definirContexto({ pedido: p, x: e.clientX, y: e.clientY });
                  }}
                  planear={() => definirAPlanear(p)}
                />
              ))}
            </ol>
          ) : null}
        </div>
      </section>

      {contexto !== null ? (
        <MenuContexto
          ctx={contexto}
          mudar={mudar}
          planear={() => {
            definirAPlanear(contexto.pedido);
            definirContexto(null);
          }}
        />
      ) : null}

      {aPlanear !== null ? (
        <PainelPlanear
          pedido={aPlanear}
          fechar={() => definirAPlanear(null)}
          nomeLocalidade={nomeLocalidade}
          nomeMotorista={nomeMotorista}
          numeroViatura={numeroViatura}
          recarregar={recarregar}
        />
      ) : null}
    </>
  );
}

/** Uma linha da lista: cores, tags e marcadores (📗 5.3.3.1). */
function Linha({
  pedido,
  subir,
  descer,
  menu,
  planear,
}: {
  pedido: Pedido;
  subir: () => void;
  descer: () => void;
  menu: (e: EventoRato<HTMLLIElement>) => void;
  planear: () => void;
}) {
  const estado = ESTADOS[pedido.estado];
  const impedimento = razaoParaNaoPlanear(pedido);
  return (
    <li
      className={`pedido pedido--${estado.papel}${pedido.activo ? '' : ' pedido--inactivo'}`}
      onContextMenu={menu}
      // Duplo clique é o atalho para o gesto principal. O botão direito continua a ser
      // o caminho que a fonte descreve; isto é um acelerador, não uma alternativa.
      onDoubleClick={impedimento === null ? planear : undefined}
    >
      <div className="pedido-prioridade">
        {/* 📗 «subir ou descer na lista a ordem pela qual vai planear os pedidos» */}
        <button type="button" className="btn btn--micro" onClick={subir} aria-label="Subir prioridade">
          <IconeChevron em={0.8} className="ic ic--cima" />
        </button>
        <span className="pedido-p" title="prioridade de 0 a 10">
          {pedido.prioridade}
        </span>
        <button type="button" className="btn btn--micro" onClick={descer} aria-label="Descer prioridade">
          <IconeChevron em={0.8} className="ic" />
        </button>
      </div>

      <div className="pedido-corpo">
        <div className="pedido-topo">
          <span className="pedido-cliente">{pedido.clienteSigla}</span>
          <span className="pedido-datas">
            {pedido.dataInicio}
            {pedido.dataFim !== pedido.dataInicio ? ` → ${pedido.dataFim}` : ''}
          </span>
          <span className={`pedido-estado pedido-estado--${estado.papel}`}>{estado.rotulo}</span>
        </div>

        <div className="pedido-linha2">
          {pedido.recolha !== null ? (
            <span className="pedido-recolha">
              {pedido.recolha.voo !== '' ? <IconeVoo em={0.9} className="ic" /> : null}
              {pedido.recolha.localidade || '?'}
              {pedido.recolha.hora !== '' ? ` ${pedido.recolha.hora}` : ''}
              {/* A hora de presença já tem a margem da fonte aplicada (+1 h / −2 h / −4 h). */}
              {pedido.recolha.horaPresenca !== '' ? (
                <em title={`margem de ${pedido.recolha.margemH} h aplicada`}>
                  {' '}
                  · presença {pedido.recolha.horaPresenca}
                </em>
              ) : null}
            </span>
          ) : (
            <span className="pedido-falta">sem local de recolha</span>
          )}
          {pedido.lotacao > 0 ? <span className="pedido-lot">{pedido.lotacao} lug.</span> : null}
        </div>

        {tags(pedido).length > 0 ? (
          <div className="pedido-tags">
            {tags(pedido).map((t) => (
              <span key={t} className={`tag tag--${t}`}>
                {t}
              </span>
            ))}
          </div>
        ) : null}

        {pedido.requisitos !== '' ? <p className="pedido-req">{pedido.requisitos}</p> : null}
      </div>

      <button
        type="button"
        className="btn btn--peq pedido-planear"
        onClick={planear}
        disabled={impedimento !== null}
        title={impedimento ?? 'ver as 10 melhores opções'}
      >
        Planear
      </button>
    </li>
  );
}

/**
 * O menu de contexto, com as quatro operações que a fonte 5.3.3.1 nomeia.
 *
 * ⚠️ As que ainda não podem funcionar aparecem **desactivadas com a razão**, em vez de
 * ausentes. Um menu que esconde o que falta faz o utilizador procurar; um menu que o
 * mostra a cinzento diz-lhe onde está o projecto.
 */
function MenuContexto({
  ctx,
  mudar,
  planear,
}: {
  ctx: Contexto;
  mudar: (id: string, f: (p: Pedido) => Pedido) => void;
  planear: () => void;
}) {
  const impedimento = razaoParaNaoPlanear(ctx.pedido);
  // Não deixar o menu sair do ecrã pela direita nem por baixo.
  const estilo = {
    left: Math.min(ctx.x, window.innerWidth - 240),
    top: Math.min(ctx.y, window.innerHeight - 260),
  };
  return (
    <div className="menu-ctx" style={estilo} role="menu">
      <button
        type="button"
        role="menuitem"
        className="menu-ctx-item menu-ctx-item--principal"
        onClick={planear}
        disabled={impedimento !== null}
        title={impedimento ?? ''}
      >
        Planear
      </button>
      <div className="menu-ctx-sep" />
      <p className="menu-ctx-grupo">Estado de planeamento</p>
      {/* 📗 «activo, inactivo, urgente, cancelado…» — e `urgente`/`activo` são
          MARCADORES, não estados: um pedido pode estar `novo` e urgente. */}
      <button
        type="button"
        role="menuitem"
        className="menu-ctx-item"
        onClick={() => mudar(ctx.pedido.id, (p) => ({ ...p, activo: !p.activo }))}
      >
        {ctx.pedido.activo ? 'Marcar inactivo' : 'Marcar activo'}
      </button>
      <button
        type="button"
        role="menuitem"
        className="menu-ctx-item"
        onClick={() => mudar(ctx.pedido.id, (p) => ({ ...p, urgente: !p.urgente }))}
      >
        {ctx.pedido.urgente ? 'Retirar urgente' : 'Marcar urgente'}
      </button>
      <button
        type="button"
        role="menuitem"
        className="menu-ctx-item menu-ctx-item--perigo"
        onClick={() => mudar(ctx.pedido.id, (p) => ({ ...p, estado: 'cancelado' }))}
      >
        Cancelar pedido
      </button>
      <div className="menu-ctx-sep" />
      <button type="button" role="menuitem" className="menu-ctx-item" disabled title="C4 — precisa de escrita">
        Copiar / duplicar…
      </button>
      <button type="button" role="menuitem" className="menu-ctx-item" disabled title="C4 — precisa de escrita">
        Mudar itinerário…
      </button>
    </div>
  );
}

/**
 * As 10 melhores opções.
 *
 * 📗 *«o sistema abre janela nova e mostra as 10 melhores opções recomendadas»*, e cada
 * uma com os **seis elementos** que a fonte enumera. Estão aqui na ordem da fonte, de
 * propósito: é o que permite ler o requisito ao lado do ecrã.
 */
function PainelPlanear({
  pedido,
  fechar,
  nomeLocalidade,
  nomeMotorista,
  numeroViatura,
  recarregar,
}: {
  pedido: Pedido;
  fechar: () => void;
  nomeLocalidade: (i: number) => string;
  nomeMotorista: (i: number) => string;
  numeroViatura: (i: number) => string;
  recarregar?: (() => void) | undefined;
}) {
  const [r, definirR] = useState<RecomendacaoRecebida | null>(null);
  const [erro, definirErro] = useState<string | null>(null);
  const [escolhida, definirEscolhida] = useState<number | null>(null);
  /**
   * O motivo escrito por opção, indexado pelo número da opção.
   *
   * ⚠️ **Por opção e não um só**: o planeador pode escrever a justificação da opção 1,
   * mudar de ideias, olhar para a 3 — e não deve perder o que escreveu. Um campo único
   * partilhado obrigava-o a reescrever, e é assim que se ensina a escrever «ok».
   */
  const [motivos, definirMotivos] = useState<Record<string, string>>({});
  // ⚠️ TRÊS estados e não um booleano: «a reservar», «reservado» e «falhou» são coisas
  // diferentes para quem espera, e um só booleano faz o ecrã dizer «pronto» quando o
  // que houve foi um erro.
  const [reserva, definirReserva] = useState<
    | { readonly estado: 'a_reservar' }
    | { readonly estado: 'feita'; readonly dias: number; readonly alocacoes: readonly number[] }
    | { readonly estado: 'falhou'; readonly porque: string; readonly tipo: string }
    | null
  >(null);
  const destino = pedido.recolha?.localidade ?? '';
  const pedidoRef = useRef(pedido.id);
  pedidoRef.current = pedido.id;

  useEffect(() => {
    const abortar = new AbortController();
    const url =
      `/api/motor/recomendar?dia0=${pedido.dia0}&dia1=${pedido.dia1}` +
      `&destino=${encodeURIComponent(destino)}&lotacao=${pedido.lotacao}` +
      `&plano=${encodeURIComponent(pedido.plano)}` +
      // F2: os requisitos em texto livre vão para o motor filtrar e avisar.
      `&requisitos=${encodeURIComponent(pedido.requisitos)}`;
    fetch(url, { signal: abortar.signal })
      .then(async (resposta) => {
        const corpo = (await resposta.json()) as RecomendacaoRecebida & { detalhe?: string };
        if (!resposta.ok) {
          definirErro(corpo.detalhe ?? `a API respondeu ${resposta.status}`);
          return;
        }
        definirR(corpo);
      })
      .catch((e: unknown) => {
        if (e instanceof Error && e.name === 'AbortError') return;
        definirErro(e instanceof Error ? e.message : String(e));
      });
    return () => abortar.abort();
  }, [pedido.dia0, pedido.dia1, pedido.lotacao, pedido.plano, destino]);

  /**
   * Cria a reserva prévia — `POST /api/plano/reservar`.
   *
   * ⚠️ **D-007: se a opção tem avisos legais, PEDE O MOTIVO.** E se o planeador não o
   * der, não reserva — a API recusaria de qualquer modo, mas perguntar aqui explica-lhe
   * porquê em vez de lhe devolver um 400.
   *
   * ⚠️ E o motivo vai com as divergências que o motor apontou, uma a uma: a lista de
   * trabalho do superadmin ratifica **uma regra de cada vez** (P-22).
   */
  const reservarOpcao = useCallback(
    async (o: RecomendacaoRecebida['opcoes'][number], indice: number): Promise<void> => {
      // Uma opção sem `viaturaId` é um subcontratado — o executor é ele.
      const executor =
        o.executor.tipo === 'subcontratado'
          ? { tipo: 'subcontratado' as const, id: o.executor.id }
          : { tipo: 'viatura' as const, viatura: o.viaturaId ?? '' };

      // ⚠️ O MOTIVO VEM DE UM CAMPO NO PAINEL, e não de um `window.prompt`.
      //
      //   A primeira versão usava `prompt` e eu escrevi no código que era «provisório».
      //   O cliente experimentou e disse o óbvio: **não é nada prático.** Um `prompt`
      //   bloqueia o navegador, esconde os avisos que se está a justificar, não deixa
      //   escrever mais do que uma linha e não se pode corrigir depois de fechado.
      //
      //   O campo abre **junto da opção**, com os avisos à vista — que é o que uma
      //   justificação precisa: ver o que se está a justificar enquanto se escreve.
      // ⚠️ O índice vem do ARGUMENTO e não de `escolhida`: `definirEscolhida(i)` é
      // assíncrono e no primeiro clique o estado ainda tem o valor anterior. Ler dali
      // dava o motivo da opção errada — ou nenhum.
      const motivo = motivos[String(indice)] ?? null;
      if (o.avisos.length > 0 && (motivo === null || motivo.trim() === '')) {
        definirReserva({
          estado: 'falhou',
          tipo: 'motivo_obrigatorio',
          porque:
            'esta opção tem avisos legais: escreva o motivo no campo abaixo antes de ' +
            'reservar (D-007, art. 10.º n.º 3)',
        });
        return;
      }

      definirReserva({ estado: 'a_reservar' });
      try {
        const resposta = await fetch('/api/plano/reservar', {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({
            pedidoId: pedido.id,
            plano: pedido.plano,
            dia0: pedido.dia0,
            dia1: pedido.dia1,
            executor,
            motoristaId: o.motoristaId,
            clienteId: null,
            itinerarioId: pedido.itinerario,
            localidadeId: destino,
            referencia: pedido.referencia,
            motivo,
            // Cada aviso do motor vira uma divergência registada. ⚠️ `regra` sai do
            // texto do aviso porque o motor ainda não a devolve estruturada — está
            // assinalado, e é o que falta para o superadmin filtrar por regra.
            divergencias: o.avisos.map((a) => ({
              regra: 'aviso_do_motor',
              valorLimite: '',
              valorEfectivo: '',
              baseLegal: a,
            })),
          }),
        });
        const corpo = (await resposta.json()) as {
          dias?: number;
          alocacoes?: number[];
          erro?: string;
          detalhe?: string;
        };
        if (!resposta.ok) {
          definirReserva({
            estado: 'falhou',
            tipo: corpo.erro ?? String(resposta.status),
            porque: corpo.detalhe ?? `a API respondeu ${resposta.status}`,
          });
          return;
        }
        definirReserva({
          estado: 'feita',
          dias: corpo.dias ?? 0,
          alocacoes: corpo.alocacoes ?? [],
        });
        // ⚠️ E O MAPA VOLTA A SER PEDIDO. Sem isto, o planeador via «reserva criada» na
        // janela e a viatura ainda LIVRE na grelha ao lado — foi a queixa do cliente, e
        // é o tipo de contradição que faz perder a confiança em tudo o resto.
        recarregar?.();
      } catch (e) {
        definirReserva({
          estado: 'falhou',
          tipo: 'rede',
          porque: e instanceof Error ? e.message : String(e),
        });
      }
    },
    [pedido, destino, motivos, recarregar],
  );

  const euros = useMemo(
    () => new Intl.NumberFormat('pt-PT', { style: 'currency', currency: 'EUR' }),
    [],
  );

  const { posicao, aoPressionar, aArrastar } = useArrastavel(() =>
    cantoInicial(
      'esquerda',
      { largura: LARGURA_OPCOES, altura: 560 },
      { largura: window.innerWidth, altura: window.innerHeight },
    ),
  );

  return (
    // ⚠️ SEM FUNDO ESCURO. A versão anterior escurecia o ecrã inteiro, o que é o
    // oposto do que o gesto pede: escolher que autocarro mobilizar decide-se a olhar
    // para o mapa — onde ele está, o que tem à volta, quem lá anda. Um véu por cima
    // do mapa apagava exactamente a informação que sustenta a decisão.
    <section
      className="janela janela--planear"
      aria-label="Opções recomendadas"
      style={{ left: posicao.x, top: posicao.y }}
    >
      <div
        className={`janela-cab${aArrastar ? ' janela-cab--agarrada' : ''}`}
        onPointerDown={aoPressionar}
      >
        <strong className="menu-titulo">
          Planear {pedido.clienteSigla} · {pedido.dataInicio}
          {pedido.dataFim !== pedido.dataInicio ? ` → ${pedido.dataFim}` : ''}
          {destino !== '' ? ` · recolha em ${destino}` : ''}
        </strong>
        <button type="button" className="btn btn--icone" onClick={fechar} aria-label="Fechar">
          <IconeFechar em={1} />
        </button>
      </div>
      <div className="janela-corpo">

        {erro !== null ? <p className="pedidos-erro">{erro}</p> : null}
        {r === null && erro === null ? <p className="pedidos-vazio">a calcular…</p> : null}

        {r !== null ? (
          <>
            {/* O funil: sem ele, uma lista curta é indistinguível de um filtro
                avariado. Foi medido a 8 ms em produção com 82 candidatos. */}
            <p className="planear-funil">
              {r.funil['frota']} viaturas → <strong>{r.funil['aposFiltros']}</strong> possíveis →{' '}
              {r.opcoes.length} opções · {r.funil['subcontratados']} subcontratados ·{' '}
              <span className="planear-ms">{r.ms} ms</span>
              {r.funil['semMotorista'] !== undefined && r.funil['semMotorista'] > 0 ? (
                <span className="planear-semmot">
                  {' '}
                  · {r.funil['semMotorista']} sem motorista elegível
                </span>
              ) : null}
            </p>

            {/* ⚠️ O aviso ANTES do clique, e não o erro depois. */}
            {r.escritaDisponivel !== true ? (
              <p className="planear-erro">
                <strong>Este ambiente não grava.</strong> Não tem base de dados ligada, logo
                comparar opções funciona mas <strong>escolher não cria reserva</strong>. Os
                botões estão desactivados de propósito — um ecrã que oferece o que o servidor
                não consegue servir é pior do que um que o diz.
              </p>
            ) : null}

            {r.opcoes.length === 0 ? (
              <p className="pedidos-erro">
                Nenhuma opção. Isto <strong>não</strong> devia acontecer — a fonte diz que se
                encontra sempre uma solução, própria ou subcontratada. Vale investigar.
              </p>
            ) : null}

            <ol className="planear-opcoes">
              {r.opcoes.map((o, i) => {
                const sub = o.executor.tipo === 'subcontratado';
                return (
                  <li
                    key={`${o.executor.tipo}${sub ? o.executor.id : o.executor.viatura}`}
                    className={`opcao${escolhida === i ? ' opcao--escolhida' : ''}${
                      o.avisos.length > 0 ? ' opcao--avisada' : ''
                    }`}
                  >
                    <span className="opcao-n">{i + 1}</span>
                    <div className="opcao-corpo">
                      <div className="opcao-topo">
                        <strong className="opcao-quem">
                          {sub
                            ? `${o.executor.nome} (subcontratado)`
                            : `Viatura ${numeroViatura(o.executor.viatura)}`}
                        </strong>
                        <span className="opcao-custo">{euros.format(o.custo.total)}</span>
                      </div>

                      {/* Os SEIS elementos da fonte 5.3.3.1, nesta ordem. */}
                      <dl className="opcao-campos">
                        <div>
                          <dt>local actual</dt>
                          <dd>{nomeLocalidade(o.localidadeActual)}</dd>
                        </div>
                        <div>
                          <dt>distância</dt>
                          <dd>
                            {o.kmAoLocal >= 0 ? `${o.kmAoLocal} km` : 'desconhecida'}
                            {o.distanciaAproximada ? <em title="por coordenadas, não rodoviária"> aprox.</em> : null}
                          </dd>
                        </div>
                        <div>
                          <dt>disponível em</dt>
                          <dd>{o.horasAteDisponivel} h</dd>
                        </div>
                        <div>
                          <dt>motorista</dt>
                          <dd>
                            {o.motorista >= 0 ? nomeMotorista(o.motorista) : '—'}
                            {/* «e seus dias disponíveis» — a segunda metade do
                                elemento 4 da fonte. 6 de 6 é o limite do art. 8.º
                                n.º 6 § 2: nunca um 7.º dia seguido. */}
                            {o.diasAoServicoNaSemana >= 0 ? (
                              <em title="dias ao serviço nos 7 dias anteriores">
                                {' '}
                                · {o.diasAoServicoNaSemana}/6 dias
                              </em>
                            ) : null}
                          </dd>
                        </div>
                        <div>
                          <dt>troca de motorista</dt>
                          <dd>{o.implicaTrocaDeMotorista ? 'sim' : 'não'}</dd>
                        </div>
                      </dl>

                      {/* P-27: explicabilidade obrigatória. Um total sem decomposição
                          não é auditável — e um motor de que se desconfia não é usado. */}
                      <details className="opcao-custo-det">
                        <summary>como se chega a {euros.format(o.custo.total)}</summary>
                        <ul>
                          {o.custo.parcelas.map((p) => (
                            <li key={p.nome}>
                              <span>{p.nome}</span>
                              <span className="opcao-parcela-valor">{euros.format(p.valor)}</span>
                              <code>{p.origem}</code>
                            </li>
                          ))}
                        </ul>
                        {o.custo.naoSomado.length > 0 ? (
                          <ul className="opcao-naosomado">
                            {o.custo.naoSomado.map((n) => (
                              <li key={n}>{n}</li>
                            ))}
                          </ul>
                        ) : null}
                      </details>

                      {/* D-007: avisa mas permite. A opção fica MARCADA, nunca removida. */}
                      {o.avisos.length > 0 ? (
                        <ul className="opcao-avisos">
                          {o.avisos.map((a) => (
                            <li key={a}>{a}</li>
                          ))}
                        </ul>
                      ) : null}

                      {/* ⚠️ O MOTIVO, NUM CAMPO JUNTO DOS AVISOS QUE JUSTIFICA.
                          Era um `window.prompt`, e o cliente disse o óbvio: não é
                          prático. Um `prompt` bloqueia o navegador, ESCONDE os avisos
                          que se está a justificar, aceita uma linha e não se corrige
                          depois de fechado. Aqui vê-se o que se justifica enquanto se
                          escreve — que é a única forma de a justificação valer algo. */}
                      {o.avisos.length > 0 ? (
                        <label className="opcao-motivo">
                          <span>
                            Motivo da divergência <strong>(obrigatório)</strong> — D-007,
                            art. 10.º n.º 3
                          </span>
                          <textarea
                            rows={2}
                            value={motivos[String(i)] ?? ''}
                            placeholder="porque é que se aceita este risco…"
                            onChange={(e) =>
                              definirMotivos((m) => ({ ...m, [String(i)]: e.target.value }))
                            }
                          />
                        </label>
                      ) : null}
                    </div>

                    {/* 📗 «O planeador escolhe uma das opções e é registada a reserva
                        prévia no mapa.» É este botão, e agora ESCREVE. */}
                    <button
                      type="button"
                      className="btn btn--peq"
                      onClick={() => {
                        definirEscolhida(i);
                        void reservarOpcao(o, i);
                      }}
                      disabled={
                        r.escritaDisponivel !== true ||
                        reserva?.estado === 'a_reservar' ||
                        reserva?.estado === 'feita'
                      }
                      title={
                        r.escritaDisponivel !== true
                          ? 'este ambiente não tem base de dados ligada — não é possível gravar'
                          : o.avisos.length > 0
                            ? 'tem avisos legais — vai pedir o motivo (D-007)'
                            : 'cria a reserva prévia no mapa'
                      }
                    >
                      {reserva?.estado === 'a_reservar' && escolhida === i
                        ? 'a reservar…'
                        : reserva?.estado === 'feita' && escolhida === i
                          ? 'reservada'
                          : 'Escolher'}
                    </button>
                  </li>
                );
              })}
            </ol>

            {reserva?.estado === 'feita' ? (
              <p className="planear-escolhida">
                <strong>Reserva prévia criada.</strong> {reserva.dias} dia(s) no mapa com a
                textura de reserva, o pedido passou a «reservado», e a decisão ficou no log de
                auditoria. ⚠️ <strong>Reserva prévia não é serviço confirmado</strong> — a
                efectivação é o passo C5.
              </p>
            ) : null}
            {reserva?.estado === 'falhou' ? (
              <p className="planear-erro">
                <strong>A reserva não foi criada.</strong> {reserva.porque}
                {reserva.tipo === 'ocupado' ? (
                  <>
                    {' '}
                    ⚠️ Foi a <strong>base de dados</strong> a recusar, não uma verificação no
                    ecrã: alguém reservou a mesma viatura para os mesmos dias. Volte a planear —
                    a lista de opções está desactualizada.
                  </>
                ) : null}
              </p>
            ) : null}

            {/* As limitações da resposta, à vista. Uma recomendação que não diz o que
                lhe falta é uma recomendação em que se acredita por engano. */}
            <details className="planear-limitacoes">
              <summary>O que esta recomendação ainda não sabe ({r.limitacoes.length})</summary>
              <ul>
                {r.limitacoes.map((l) => (
                  <li key={l}>{l}</li>
                ))}
              </ul>
            </details>
          </>
        ) : null}
      </div>
    </section>
  );
}
